import React, { useState, useEffect } from 'react';
import ActionModal from '../../../components/ActionModal';
import { styles } from './adminStyles';

const ACTION_TYPE_LABELS = {
  field_based: 'Field-Based',
  whole_farm_manual: 'Whole Farm (Manual)',
  whole_farm_non_manual: 'Whole Farm (Non-Manual)',
};

const SPATIAL_AREA_TARGETING_LABELS = {
  arable_in_zone: 'Arable in Zone',
  arable_and_grassland_in_zone: 'Arable & Grassland in Zone',
};

const ActionLibrary = ({
  libraryActions,
  isActionModalOpen, setIsActionModalOpen, editingAction, setEditingAction,
  handleSaveAction, handleDeleteAction,
  archivedActions, loadArchivedActions, handleArchiveAction, handleRestoreAction,
  wholeFarmModalMode, setWholeFarmModalMode, openWholeFarmModal,
  algorithms, onOpenAlgorithmReport, onAlgorithmCreated
}) => {
  const [showArchivedView, setShowArchivedView] = useState(false);

  useEffect(() => {
    if (showArchivedView) loadArchivedActions();
  }, [showArchivedView, loadArchivedActions]);

  const visibleActions = showArchivedView ? archivedActions : libraryActions;

  return (
    <div style={styles.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div>
                <h2 style={{ margin: 0, color: '#0F172A', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>Action Library</h2>
                <p style={{ margin: 0, color: '#475569', fontSize: '0.9rem' }}>Manage the environmental actions available for events.</p>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
                <button
                    onClick={onOpenAlgorithmReport}
                    style={{ backgroundColor: '#FFFFFF', color: '#0D9488', padding: '10px 20px', border: '1px solid #0D9488', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontFamily: '"Inter", sans-serif' }}
                >
                    Action-Algorithm Report
                </button>
                <button
                    onClick={() => { setEditingAction(null); setWholeFarmModalMode(false); setIsActionModalOpen(true); }}
                    style={{ backgroundColor: '#0D9488', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontFamily: '"Inter", sans-serif' }}
                >
                    + Create Field Action
                </button>
                <button
                    onClick={openWholeFarmModal}
                    style={{ backgroundColor: '#FFFFFF', color: '#0D9488', padding: '10px 20px', border: '1px solid #0D9488', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontFamily: '"Inter", sans-serif' }}
                >
                    + Create Whole Farm Action
                </button>
            </div>
        </div>

        <div style={{ display: 'flex', gap: '30px', borderBottom: '2px solid #ddd', marginBottom: '20px' }}>
            <button
                onClick={() => setShowArchivedView(false)}
                style={{ background: 'none', border: 'none', borderBottom: !showArchivedView ? '3px solid #0D9488' : '3px solid transparent', color: !showArchivedView ? '#0F172A' : '#475569', fontWeight: 'bold', padding: '10px 5px', fontSize: '1rem', cursor: 'pointer', fontFamily: '"Inter", sans-serif', transform: 'translateY(2px)' }}
            >
                Active Actions
            </button>
            <button
                onClick={() => setShowArchivedView(true)}
                style={{ background: 'none', border: 'none', borderBottom: showArchivedView ? '3px solid #0D9488' : '3px solid transparent', color: showArchivedView ? '#0F172A' : '#475569', fontWeight: 'bold', padding: '10px 5px', fontSize: '1rem', cursor: 'pointer', fontFamily: '"Inter", sans-serif', transform: 'translateY(2px)' }}
            >
                Archived Actions
            </button>
        </div>

        <div style={{ overflowX: 'auto', backgroundColor: '#FFFFFF', borderRadius: '6px', border: '1px solid #E2E8F0' }}>
            <table style={{ ...styles.table, width: '100%' }}>
                <thead>
                    <tr style={{ backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0' }}>
                        <th style={{...styles.th, textAlign: 'left'}}>Action Name</th>
                        <th style={{...styles.th, textAlign: 'center'}}>Unit</th>
                        <th style={{...styles.th, textAlign: 'center'}}>Action Type</th>
                        <th style={{...styles.th, textAlign: 'left', minWidth: '130px'}}>Outcome Type</th>
                        <th style={{...styles.th, textAlign: 'left', minWidth: '180px'}}>Outcome Algorithm</th>
                        <th style={{...styles.th, textAlign: 'center', maxWidth: '120px'}}>Land Use Multipliers</th>
                        <th style={{...styles.th, textAlign: 'left'}}>Short Description</th>
                        <th style={{...styles.th, textAlign: 'center'}}>Update</th>
                    </tr>
                </thead>
                <tbody>
                    {visibleActions.map(action => {
                        const multipliersObj = action.parcelTypeMultipliers || {};
                        const multiplierEntries = Object.entries(multipliersObj);
                        const multiplierText = multiplierEntries.length > 0
                            ? multiplierEntries.map(([key, val]) => `${key}: ${val}x`).join(', ')
                            : '-';

                        const outcomesArr = action.outcomes || [];
                        const outcomeTypeText = outcomesArr.length > 0
                            ? outcomesArr.map(o => {
                                const algorithm = (algorithms || []).find(alg => alg._id === o.outcomeAlgorithmId);
                                return algorithm?.outcomeType || 'Unknown';
                            }).join(' | ')
                            : 'None';
                        const outcomeAlgorithmText = outcomesArr.length > 0
                            ? outcomesArr.map(o => {
                                const algorithm = (algorithms || []).find(alg => alg._id === o.outcomeAlgorithmId);
                                return `${algorithm?.name || 'Unknown Algorithm'}${algorithm?.formulaString ? ` (${algorithm.formulaString})` : ''}`;
                            }).join(' | ')
                            : 'None';

                        const actionTypeLabel = ACTION_TYPE_LABELS[action.actionType] || ACTION_TYPE_LABELS.field_based;
                        const spatialAreaTargetingLabel = SPATIAL_AREA_TARGETING_LABELS[action.spatialAreaTargeting];

                        return (
                            <tr key={action._id} style={{ borderBottom: '1px solid #E2E8F0' }}>
                                <td style={{...styles.td, fontWeight: 'bold', color: '#0F172A', textAlign: 'left'}}>{action.name}</td>
                                <td style={{...styles.td, textAlign: 'center'}}>{action.unit}</td>
                                <td style={{...styles.td, textAlign: 'center'}}>
                                    <span style={{ backgroundColor: '#E0F2FE', color: '#0369A1', padding: '4px 8px', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 'bold' }}>
                                        {actionTypeLabel.toUpperCase()}
                                    </span>
                                    {spatialAreaTargetingLabel && (
                                        <div style={{ fontSize: '0.7rem', color: '#475569', marginTop: '4px' }}>{spatialAreaTargetingLabel}</div>
                                    )}
                                </td>
                                <td style={{...styles.td, textAlign: 'left', fontSize: '0.85rem', color: '#475569'}}>
                                    {outcomeTypeText}
                                </td>

                                <td style={{...styles.td, textAlign: 'left', fontSize: '0.8rem', color: '#475569', whiteSpace: 'normal', wordWrap: 'break-word'}}>
                                    {outcomeAlgorithmText}
                                </td>

                                <td style={{...styles.td, textAlign: 'center', color: '#475569', fontSize: '0.8rem', maxWidth: '120px', whiteSpace: 'normal', wordWrap: 'break-word'}}>
                                    {multiplierText}
                                </td>

                                <td style={{...styles.td, textAlign: 'left', color: '#475569', fontSize: '0.85rem', whiteSpace: 'normal', wordWrap: 'break-word'}}>
                                    {action.shortDescription || <span style={{ fontStyle: 'italic', color: '#94A3B8' }}>No description</span>}
                                </td>

                                <td style={{...styles.td, textAlign: 'center', whiteSpace: 'nowrap'}}>
                                    <button
                                        onClick={() => { setEditingAction(action); setWholeFarmModalMode(false); setIsActionModalOpen(true); }}
                                        style={{ padding: '6px 12px', border: 'none', backgroundColor: '#0D9488', color: '#FFFFFF', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem', marginRight: '10px' }}
                                    >
                                        Edit
                                    </button>
                                    {showArchivedView ? (
                                        <button
                                            onClick={() => handleRestoreAction(action._id)}
                                            style={{ padding: '6px 12px', border: 'none', backgroundColor: '#0D9488', color: '#FFFFFF', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem', marginRight: '10px' }}
                                        >
                                            Restore
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => handleArchiveAction(action._id)}
                                            style={{ padding: '6px 12px', border: '1px solid #0D9488', backgroundColor: '#FFFFFF', color: '#0D9488', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem', marginRight: '10px' }}
                                        >
                                            Archive
                                        </button>
                                    )}
                                    <button
                                        onClick={() => handleDeleteAction(action._id, action.name)}
                                        style={{ padding: '6px 12px', border: 'none', backgroundColor: '#0D9488', color: '#F59E0B', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem' }}
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        );
                    })}
                    {visibleActions.length === 0 && (
                        <tr><td colSpan="8" style={{ padding: '20px', textAlign: 'center', color: '#64748B' }}>
                            {showArchivedView ? 'No archived actions.' : 'No actions found in the library.'}
                        </td></tr>
                    )}
                </tbody>
            </table>
        </div>

        {isActionModalOpen && (
            <ActionModal
                existingAction={editingAction}
                onClose={() => { setIsActionModalOpen(false); setEditingAction(null); setWholeFarmModalMode(false); }}
                onSave={handleSaveAction}
                forceWholeFarm={wholeFarmModalMode}
                algorithms={algorithms}
                onAlgorithmCreated={onAlgorithmCreated}
            />
        )}
    </div>
  );
};

export default ActionLibrary;
